import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  items: [],
  count: {
    total: 0,
    unread: 0,
    by_source: {
      communication: 0,
    },
  },
  unreadCount: 0,
  isFetching: false,
  hasLoaded: false,
  error: false,
  errorMessage: null,
};

const buildCountFromItems = (items = []) => {
  const unreadItems = items.filter((item) => !item?.is_read);
  const unread = unreadItems.length;

  return {
    total: unread,
    unread,
    by_source: {
      communication: unread,
    },
  };
};

const normalizeItems = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const normalizeCommunicationItem = (item) => {
  if (!item || typeof item !== "object") {
    return null;
  }

  const sourceId = Number(item?.source_id ?? item?.notification_id);
  const fallbackId = Number.isFinite(sourceId) ? sourceId : null;

  return {
    ...item,
    uuid:
      item?.uuid ??
      (fallbackId !== null ? String(fallbackId) : item?.id ?? null),
    source_id: fallbackId ?? item?.source_id ?? null,
    notification_id: fallbackId ?? item?.notification_id ?? null,
    };
};

const communicationNotificationsSlice = createSlice({
  name: "communicationNotifications",
  initialState,
  reducers: {
    communicationNotificationsStart: (state) => {
      state.isFetching = true;
      state.error = false;
      state.errorMessage = null;
    },
    communicationNotificationsSuccess: (state, action) => {
      state.isFetching = false;
      state.hasLoaded = true;
      state.error = false;
      state.errorMessage = null;
      state.items = normalizeItems(action.payload)
        .map(normalizeCommunicationItem)
        .filter(Boolean);
      state.count =
        action.payload?.count ||
        buildCountFromItems(state.items);
      state.unreadCount =
        Number(action.payload?.count?.unread) ||
        Number(action.payload?.count?.by_source?.communication) ||
        buildCountFromItems(state.items).unread;
    },
    communicationNotificationsFailure: (state, action) => {
      state.isFetching = false;
      state.hasLoaded = true;
      state.error = true;
      state.errorMessage = action.payload || null;
    },
    communicationNotificationMarkedRead: (state, action) => {
      const id = Number(action.payload?.id);

      if (!Number.isFinite(id)) {
        return;
      }

      state.items = state.items.map((item) => {
        const sourceId = Number(item?.source_id ?? item?.notification_id);
        if (sourceId === id) {
          return {
            ...item,
            is_read: true,
          };
        }

        return item;
      });

      state.count = buildCountFromItems(state.items);
      state.unreadCount = state.count.unread;
    },
    communicationNotificationsMarkedAllRead: (state) => {
      state.items = state.items.map((item) => ({
        ...item,
        is_read: true,
      }));
      state.count = buildCountFromItems(state.items);
      state.unreadCount = state.count.unread;
    },
    clearCommunicationNotifications: () => initialState,
  },
});

export const {
  communicationNotificationsStart,
  communicationNotificationsSuccess,
  communicationNotificationsFailure,
  communicationNotificationMarkedRead,
  communicationNotificationsMarkedAllRead,
  clearCommunicationNotifications,
} = communicationNotificationsSlice.actions;

export default communicationNotificationsSlice.reducer;
